import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, ArrowRight, Zap } from "lucide-react";

type SetupState = {
  stripeConnection: unknown | null;
  hubspotConnected: boolean;
  hasProducts: boolean;
};

type Step = {
  label: string;
  done: boolean;
  href: string;
  cta: string;
};

export function SetupBanner() {
  const [state, setState] = useState<SetupState | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/settings/state", {
          credentials: "include",
        });
        if (!res.ok) return;
        const data = (await res.json()) as SetupState;
        if (!cancelled) setState(data);
      } catch {
        // no-op
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!state) return null;

  const steps: Step[] = [
    {
      label: "Connect Stripe",
      done: state.stripeConnection !== null,
      href: "/settings",
      cta: "Go to Settings",
    },
    {
      label: "Add products to track",
      done: state.hasProducts,
      href: "/settings",
      cta: "Go to Settings",
    },
    {
      label: "Connect HubSpot",
      done: state.hubspotConnected,
      href: "/gtm-engine",
      cta: "Go to GTM Engine",
    },
  ];

  const remaining = steps.filter((s) => !s.done);
  if (remaining.length === 0) return null;

  const next = remaining[0];
  const completedCount = steps.length - remaining.length;

  return (
    <div className="border-b border-amber-200 bg-amber-50 px-6 py-3">
      <div className="mx-auto flex max-w-6xl items-center gap-4">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100">
          <Zap className="h-4 w-4 text-amber-600" />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-amber-900">
            Finish setting up &mdash; {completedCount} of {steps.length} complete
          </p>

          <div className="mt-1.5 flex flex-wrap items-center gap-x-5 gap-y-1">
            {steps.map((step) => (
              <span
                key={step.label}
                className="flex items-center gap-1.5 text-xs"
              >
                {step.done ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                ) : (
                  <Circle className="h-3.5 w-3.5 text-amber-400" />
                )}
                <span
                  className={step.done ? "text-emerald-700" : "text-amber-800"}
                >
                  {step.label}
                </span>
              </span>
            ))}
          </div>
        </div>

        <Link
          to={next.href}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition-colors hover:bg-amber-700"
        >
          {next.cta}
          <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    </div>
  );
}
